import { forwardRef, type HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./lib/cn.js";

/**
 * Badge — small inline status pill in the scifi vocabulary.
 *
 * Sharp corners + hairline border + mono caps with --pp-tracking-caps.
 * Mirrors the overlay's StatusBadges pattern so spectator chrome and
 * app chrome share the same status indicator look.
 *
 * Variants:
 *   - `default`     — primary-tone fill, text on accent.
 *   - `secondary`   — translucent strong-bg fill.
 *   - `destructive` — danger fill.
 *   - `accent`      — accent fill.
 *   - `outline`     — hairline border, transparent fill. Most common.
 *   - `warn`        — warn-tinted border + text (no fill).
 *   - `danger`      — danger-tinted border + text (no fill).
 *   - `muted`       — muted border + text.
 */
const badgeVariants = cva(
  [
    "inline-flex items-center gap-1 px-2 py-0.5",
    "font-[var(--pp-font-family)] font-[var(--pp-weight-bold)] uppercase tracking-[var(--pp-tracking-caps)] leading-none",
    "text-[var(--pp-font-sm)] whitespace-nowrap",
    "border border-[var(--pp-border)]",
    "transition-colors",
  ],
  {
    variants: {
      variant: {
        default:
          "bg-[var(--primary)] text-[var(--pp-text-on-accent)] border-[var(--primary)]",
        secondary:
          "bg-[var(--pp-bg-strong)] text-[var(--pp-text)] border-[var(--pp-border)]",
        destructive:
          "bg-[var(--pp-danger)] text-[var(--pp-text-on-accent)] border-[var(--pp-danger)]",
        accent:
          "bg-[var(--accent)] text-[var(--pp-text-on-accent)] border-[var(--accent)]",
        outline:
          "bg-transparent text-[var(--pp-text)] border-[var(--pp-border)]",
        warn:
          "bg-transparent text-[var(--pp-warn)] border-[var(--pp-warn)]",
        danger:
          "bg-transparent text-[var(--pp-danger)] border-[var(--pp-danger)]",
        muted:
          "bg-transparent text-[var(--pp-text-muted)] border-[var(--pp-border)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  variant?:
    | "default"
    | "secondary"
    | "destructive"
    | "accent"
    | "outline"
    | "warn"
    | "danger"
    | "muted";
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => (
    <span
      ref={ref}
      data-pp-badge
      data-pp-variant={variant ?? "default"}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  ),
);

Badge.displayName = "Badge";

export { badgeVariants };
